import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from "react-router-dom";
import UserForm from './UserForm';
import { Text, Spinner, SpinnerSize } from "@fluentui/react";
import { Separator } from 'office-ui-fabric-react/lib/Separator';
import DocumentTitle from 'react-document-title';
import Auth from "../../Auth/Auth";
import SubscriptionService, { SubscriptionType } from '@/services/SubscriptionService';
import UsersSubscriptionService, { UsersSubscriptionType } from '@/services/UsersSubscriptionService';
import UserService, { UserType } from '@/services/UserService';
import BackButton from "../buttons/BackButton";
import './Subscriptions.sass';
import UserIdentitiesService, { UserIdentityType } from '@/services/UserIdentitiesService';
import { decimalFormatter } from '@/utils/numberFormatter';
import { buildIdentityPatch, getIdentityPatchErrorMessage, getSoleMainNewDraft, isIdentityReadOnly, selectDisplayIdentity, selectEditableIdentity, shouldSendIdentity } from './subscriptionIdentityRules';

const Subscription = () =>
{
    const [error, setError] = useState({ message: "" });
    const [isLoaded, setIsLoaded] = useState(false);
    const [subscription, setSubscription] = useState<Partial<SubscriptionType>>({});
    const [user] = useState<UserType>(Auth.getUserProfile());
    const [userIdentity, setUserIdentity] = useState<UserIdentityType>(user.mainUserIdentity);
    // Intervinientes del usuario: undefined mientras se cargan
    const [identities, setIdentities] = useState<UserIdentityType[] | undefined>(undefined);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const { subscriptionId } = useParams<any>();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const history = useHistory();
    const { state } = useLocation<any>();
    let initialContribution: any = state.initialContribution;
    let managedPortfolioId: any = state.managedPortfolioId;
    let bmePortfolioId: any = state.bmePortfolioId;

    useEffect(() =>
    {
        setIsLoaded(false);
        SubscriptionService.getSubscription(subscriptionId).then((data: SubscriptionType) =>
        {
            setIsLoaded(true);
            setSubscription(data);
        },
        (error) =>
        {
            setIsLoaded(true);
            setError(error);
        });
    }, [subscriptionId]);

    // Siempre se consultan los intervinientes: deciden si se identifica al cliente o se muestran sus datos en lectura
    useEffect(() =>
    {
        let isCancelled = false;
        setIdentities(undefined);

        if(user.id === undefined)
        {
            setError({ message: "No se ha podido identificar al usuario" });
            return;
        }

        UserIdentitiesService.getIdentitiesByUserId(user.id).then((data: UserIdentityType[]) =>
        {
            if(isCancelled)
            {
                return;
            }

            const loadedIdentities = Array.isArray(data) ? data : [];
            const mainIdentity = loadedIdentities.find(identity => identity.main);
            if(mainIdentity && user.mainUserIdentity == null)
            {
                // Se conserva el refresco del perfil local cuando no traía el interviniente principal
                Auth.setUserProfile({mainUserIdentity: mainIdentity});
            }
            setIdentities(loadedIdentities);
        },
        (error) =>
        {
            if(!isCancelled)
            {
                setError(error);
            }
        });

        return () =>
        {
            isCancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id]);

    const isIdentitiesLoaded = identities !== undefined;
    const isReadOnlyIdentity = isIdentityReadOnly(identities);
    // En lectura, el principal (o el perfil); editable, el principal NEW a rellenar completado con el perfil, o el perfil
    const formUser: UserType = isReadOnlyIdentity
        ? { ...user, mainUserIdentity: selectDisplayIdentity(identities, user.mainUserIdentity) as UserIdentityType }
        : { ...user, mainUserIdentity: selectEditableIdentity(identities, user.mainUserIdentity) as UserIdentityType };
    const isSubmitDisabled = !isIdentitiesLoaded || isButtonDisabled;

    const goToPayment = () =>
    {
        UsersSubscriptionService.createUsersSubscription(
        {
            userId: user.id,
            subscriptionId: parseInt(subscriptionId)
        })
        .then((usersSubscription: UsersSubscriptionType) =>
        {
            setIsLoaded(true);

            var url = `/payment?subscriptionId=${subscriptionId}&usersSubscriptionId=${usersSubscription.id}`;

            if(initialContribution !== undefined)
            {
                url += `&initialContribution=${initialContribution}`;
            }
            if(managedPortfolioId !== undefined)
            {
                url += `&managedPortfolioId=${managedPortfolioId}`;
            }
            if(bmePortfolioId !== undefined)
            {
                url += `&bmePortfolioId=${bmePortfolioId}`;
            }

            history.push(url);
        },
        (error) =>
        {
            setIsLoaded(true);
            setError(error);
        });
    };

    const createOrder = (event: any) =>
    {
        event.preventDefault();

        if(isSubmitDisabled || user.id === undefined)
        {
            return;
        }

        setIsSubmitted(true);

        // Solo se identifica al cliente si no tiene ningún interviniente (alta del principal) o si el único es su principal
        // en NEW (se rellena el que nace vacío con el registro).
        // Solo viajan nombre, apellidos, email, prefijo y teléfono; el Status, la fecha y los teléfonos de firma/operaciones no.
        if(!shouldSendIdentity(identities))
        {
            goToPayment();
            return;
        }

        const mainNewDraft = getSoleMainNewDraft(identities);
        UserService.updateUser(buildIdentityPatch(user.id, userIdentity)).then((updatedUser: UserType) =>
        {
            const updatedMain = updatedUser?.mainUserIdentity;
            if(mainNewDraft !== undefined && updatedMain?.id === mainNewDraft.id)
            {
                // El perfil local seguía con el principal vacío: se actualiza con el que devuelve el backend para que el
                // resto de pantallas (Primeros pasos, carrito...) lo vean identificado sin esperar a recargar el perfil.
                // Se usa la respuesta y no lo enviado: un backend anterior ignora la identidad y lo devuelve sin cambios.
                Auth.setUserProfile({ mainUserIdentity: updatedMain });
            }
            setIsLoaded(true);
            goToPayment();
        },
        (error) =>
        {
            setIsLoaded(true);
            // El 400 por datos obligatorios del interviniente principal se muestra con un mensaje comprensible
            const requiredFieldsMessage = getIdentityPatchErrorMessage(error);
            setError(requiredFieldsMessage !== undefined ? { message: requiredFieldsMessage } : error);
        });
    };

    function getFrequency(freq: any)
    {
        var result: string = String(freq);
        if(result === "month")
        {
            return "mes";
        }
        else
        {
            return "año";
        }
    }

    if(error.message)
    {
        return <div>Error: {error.message}</div>;
    }
    else
    {
        if(!isLoaded || subscription === undefined)
        {
            return <Spinner size={SpinnerSize.large} />
        }
        else
        {
            return(
                <DocumentTitle title={`IronIA - Suscripción ${subscription.name}`}>
                    <React.Fragment>
                        <div className="ms-Grid-row">
                            <BackButton />
                        </div>
                        <div className="ms-Grid-row subscription-strait">
                            <h2>Suscríbete ya a IronIA Fintech</h2>
                            <p className="description">Inserta tus datos personales, realiza el pago y empieza a invertir en IronIA.</p>
                            <Separator />
                            <p className="fatca-notice">
                                No podemos permitir el alta en ningún nuevo plan a usuarios con residencia fiscal en Estados Unidos o que estén sujetos a obligaciones de reporte FATCA. Si te encuentras en alguna de estas situaciones, no podrás continuar con el proceso de alta.
                            </p>
                        </div>
                        <div className="ms-Grid-row subscription-strait">
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12 ms-lg8">
                                    <div className="shadow medium">
                                        <h3>
                                            <span className="red circled small">1</span> Datos del Usuario Acceso IronIA
                                        </h3>
                                        <Text block className="legend">
                                            Los titulares de los planes de cartera pueden ser diferentes a los intervinientes de los contratos,
                                            el titular del plan de cartera es el titular de la suscripción:
                                        </Text>
                                        {!isIdentitiesLoaded &&
                                        (
                                            <Spinner size={SpinnerSize.large} />
                                        )}
                                        {isIdentitiesLoaded &&
                                        (
                                            <UserForm user={formUser} readOnly={isReadOnlyIdentity} setUserIdentity={setUserIdentity} setIsButtonDisabled={setIsButtonDisabled} />
                                        )}
                                    </div>
                                </div>
                                <div className="ms-Grid-col ms-sm12 ms-lg4" style={{paddingLeft: "2em !important"}}>
                                    <div className="shadow medium summary">
                                        <h3>Resumen del pedido</h3>
                                        <Text>Plan<span className="right">{subscription.name}</span></Text>
                                        <Separator />
                                        {!!subscription.freePeriods &&
                                        (
                                            <>
                                                <Text>Prueba gratuita<span className="right">{subscription.freePeriods} {getFrequency(subscription.period)}</span></Text>
                                                <Separator />
                                            </>
                                        )}
                                        {subscription.period === 'month' &&
                                        (
                                            <Text block className="total">Facturación Mensual<span className="right">{decimalFormatter.format(subscription.price as number)}€</span></Text>
                                        )}
                                        {(subscription.period === 'year') &&
                                        (
                                            <Text block className="total">Facturación Anual<span className="right">{decimalFormatter.format(subscription.price as number)}€</span></Text>
                                        )}
                                        {!isSubmitted &&
                                        (
                                            <button className={"button secondary-button " + (isSubmitDisabled ? 'disabled' : '')} disabled={isSubmitDisabled} onClick={(event) => { createOrder(event) }}>Realizar pago</button>
                                        )}
                                        {isSubmitted &&
                                        (
                                            <Spinner size={SpinnerSize.large} />
                                        )}
                                    </div>
                                    <span className="legend sublegend">
                                        Los planes de cartera asocian suscripciones y contratos con diferentes intervinientes que, según la legislación española, pueden ser menores,
                                        empresas, etc. En este paso del proceso de alta hacemos referencia al titular del plan de cartera, no a los intervinientes de los contratos,
                                        que se especificarán en los siguientes pasos.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                </DocumentTitle>
            );
        }
    }
};

export default Subscription;